/**
 * @file     promptStyles.ts
 * @purpose  Shared look for the passive prompt surfaces and the booking sheet:
 *           colours from the hub kit, the prompt panel (regular glass, radius
 *           20) and the button styles.
 */

import { hubColors } from '@/components/hub/hubKitStyles';
import { colorPalettesByMode, glassMaterial, layoutTokens } from '@/design-system/tokens';
import { PROMPT_LAYOUT, PROMPT_MOTION } from '@/core/config/prompts';

export const promptColors = {
  ...hubColors,
  /** The QR tile and the app icon stay the same in both themes. */
  qrPaper: colorPalettesByMode.light.neutral[0],
  qrEdge: colorPalettesByMode.light.neutral[100],
  qrInk: colorPalettesByMode.light.neutral[900],
  appTile: colorPalettesByMode.light.brand.primary,
  appMark: colorPalettesByMode.light.neutral[0],
  errorSoft: `color-mix(in srgb, ${hubColors.statusError} 10%, transparent)`,
  successSoft: hubColors.successSoft,
} as const;

export const promptRadius = layoutTokens.radius;

/** Below this the card becomes the bar, and the app panel its phone shape. */
export const PROMPT_WIDE_QUERY = '@media (min-width: 900px)';

/**
 * The panel every passive prompt draws. BottomSlot owns the fixed position,
 * the inset above sticky bars, the layer and the enter and exit motion.
 */
export function promptPanelSx(shape: 'card' | 'bar') {
  return {
    ...(shape === 'card'
      ? { width: PROMPT_LAYOUT.card.width, maxWidth: `calc(100vw - ${PROMPT_LAYOUT.card.inset * 2}px)` }
      : { width: '100%' }),
    ...glassMaterial('regular'),
    borderRadius: `${promptRadius.lg}px`,
    color: promptColors.textPrimary,
  } as const;
}

export const focusRing = {
  '&:focus-visible': { outline: `2px solid ${promptColors.interactiveFocusRing}`, outlineOffset: 2 },
} as const;

const buttonReset = {
  appearance: 'none',
  border: 0,
  m: 0,
  font: 'inherit',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  transition: `background-color ${PROMPT_MOTION.exitMs}ms ${PROMPT_MOTION.exitEase}, border-color ${PROMPT_MOTION.exitMs}ms ${PROMPT_MOTION.exitEase}`,
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  ...focusRing,
} as const;

export function primaryButtonSx(height: number, radius: number) {
  return {
    ...buttonReset,
    minHeight: height,
    px: 3.5,
    borderRadius: `${radius}px`,
    backgroundColor: promptColors.interactivePrimary,
    color: promptColors.textOnPrimary,
    fontWeight: 500,
    '&:hover': { backgroundColor: promptColors.primaryHover },
  } as const;
}

export function ghostButtonSx(height: number, radius: number) {
  return {
    ...buttonReset,
    minHeight: height,
    px: 4,
    borderRadius: `${radius}px`,
    backgroundColor: 'transparent',
    color: promptColors.textSecondary,
    fontWeight: 500,
    '&:hover': { backgroundColor: promptColors.interactiveHover },
  } as const;
}

export const iconButtonSx = {
  ...buttonReset,
  flexShrink: 0,
  width: 44,
  height: 44,
  p: 0,
  borderRadius: `${promptRadius.sm}px`,
  backgroundColor: 'transparent',
  color: promptColors.textMuted,
  '&:hover': { backgroundColor: promptColors.interactiveHover },
} as const;

export const ellipsisSx = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } as const;
